'use client'

import {
	Contentsidebar,
	ContentsidebarContent,
	ContentsidebarSidebar
} from 'layout/sidebar/contentsidebar'
import {DPBlock, DPImage, DPRelatedBlock} from 'types/DPTypes'

import {Blocks} from 'blocks/blocks'
import {Filters} from 'layout/partials/filters'
import {Link} from 'util/link'
import NextImage from 'next/image'
import {Paragraph} from 'blocks/paragraph'
import {Related} from 'blocks/related/related'
import {SearchresultsMore} from 'layout/search/searchresults'
import {Share} from 'blocks/share/share'
import {Textblock} from 'blocks/textblock/textblock'
import {Theme} from 'layout/theme'
import css from './euprojects.module.scss'
import {fromModule} from 'util/styler/Styler'
import {useApp} from 'AppContext'
import {useImecSearchParams} from 'util/useImecSearchParams'
import {useTranslation} from 'util/i18'

const styles = fromModule(css)
const euProjectFilterKeys = ['domain', 'scope']

export type EuProjectsItemType = {
	id: string
	title: string
	description: string
	url: string
	link?: string
	agreement_id?: string
	domain: string
	scope: string
	body?: string
}

export type EuProjectsFilters = {
	domain: string[]
	scope: string[]
}

const filterProjects = (
	filters: EuProjectsFilters,
	projects: EuProjectsItemType[],
	termsSet: Set<number>
) => {
	const activeFilters: EuProjectsFilters = {
		domain: [],
		scope: []
	}
	let termOptionId = 0
	for (let projectFilter of euProjectFilterKeys) {
		for (let term of filters[projectFilter]) {
			termOptionId++
			if (termsSet.has(termOptionId)) {
				activeFilters[projectFilter].push(term)
			}
		}
	}

	return projects.filter((p) => {
		for (let filterKey of euProjectFilterKeys) {
			if (!activeFilters[filterKey].length) continue
			if (!p[filterKey]) return false
			if (activeFilters[filterKey].indexOf(p[filterKey]) >= 0) continue
			return false
		}
		return true
	})
}

export const EuProjects: React.FC<{
	projects: EuProjectsItemType[]
	filters: {
		domain: string[]
		scope: string[]
	}
}> = ({projects, filters}) => {
	const {euprojects} = useTranslation()
	const {page, termsSet, updateParams} = useImecSearchParams()

	const filteredProjects = filterProjects(filters, projects, termsSet)
	const visibleProjects = filteredProjects.slice(0, (page + 1) * 10)

	let termOptionId = 0
	const filterData = euProjectFilterKeys.map((key) => ({
		title: euprojects.filters[key],
		key: key,
		options: filters[key].map((term) => {
			termOptionId++
			return {
				id: termOptionId,
				name: key === 'scope' ? euprojects.scopes[term] : term
			}
		})
	}))

	return (
		<div className={styles.euprojects()}>
			<Filters filters={filterData} />
			<div className={styles.euprojects.space()} />
			<div className={styles.euprojects.content()}>
				{visibleProjects.map((p, i) => (
					<EuProjectsItem item={p} key={p.id + '' + i} />
				))}
			</div>
			{visibleProjects.length < filteredProjects.length && (
				<SearchresultsMore onClick={() => updateParams({page: page + 1})} />
			)}
		</div>
	)
}

export const EuProjectsItem: React.FC<{item: EuProjectsItemType}> = ({
	item
}) => {
	const t = useTranslation()
	return (
		<div className={styles.item()}>
			<Theme.Container>
				<div className={styles.item.content()}>
					<Link href={item.url} className={styles.item.content.title()}>
						{item.title}
					</Link>
					<Theme.Wysiwyg className={styles.item.content.description()}>
						{item.description}
					</Theme.Wysiwyg>
					<Link href={item.url} className={styles.item.content.link()}>
						{t.iconoverview.readmore}
					</Link>
				</div>
			</Theme.Container>
		</div>
	)
}

export const EuProjectsDetail: React.FC<{
	project: EuProjectsItemType
	filters: EuProjectsFilters
	euprojects_slug: string
	blocks?: DPBlock[]
	logo?: DPImage
}> = ({project, filters, euprojects_slug, blocks = [], logo = null}) => {
	const t = useTranslation()
	const {lang} = useApp()

	const getDomainUrl = () => {
		let termOptionId = 0
		for (let projectFilter of euProjectFilterKeys) {
			for (let term of filters[projectFilter]) {
				termOptionId++
				if (project.domain !== term) continue
				return `/${lang}${euprojects_slug}?terms%5B%5D=${termOptionId}`
			}
		}
		return `/${lang}${euprojects_slug}`
	}

	const getImgName = () => {
		if (project.id === 'lightup') return 'logo_ep_interreg.png'
		if (project.link.indexOf('www.interregemr.eu') >= 0)
			return 'logo_ep_interregemr.png'
		return `logo_ep_${project.scope}.svg`
	}

	const relatedBlock = blocks.find(
		(bl) => bl._type === 'related'
	) as DPRelatedBlock

	return (
		<div className={styles.detail()}>
			<Contentsidebar className={styles.detail.contentsidebar()}>
				<ContentsidebarContent>
					<Blocks fullwidth={true} blocks={blocks}>
						<Textblock
							_type={'textblock'}
							id={0}
							display_dark={false}
							display_grey={false}
							text={project.body}
						/>
						{blocks
							.filter((bl) => bl._type !== 'related')
							.map((block) => (
								<Paragraph {...block} firstBlock={false} key={block.id} />
							))}
					</Blocks>
				</ContentsidebarContent>
				<ContentsidebarSidebar>
					<div className={styles.detail.contentsidebar.sidebar()}>
						{logo ? (
							<NextImage
								{...logo}
								sizes="460px"
								className={styles.detail.logo()}
							/>
						) : (
							// eslint-disable-next-line @next/next/no-img-element
							<img
								src={`/assets/img/${getImgName()}`}
								className={styles.detail.logo()}
							/>
						)}
						{project.link !== 'n/a' && (
							<Link href={project.link} className={styles.detail.link()}>
								{project.link}
							</Link>
						)}
						{project.agreement_id && (
							<>
								<div className={styles.detail.title()}>Grant agreement ID:</div>
								<p>{project.agreement_id}</p>
							</>
						)}

						{!!project.domain && (
							<>
								<div className={styles.detail.title()}>
									{t.euprojects.domain}:
								</div>
								<Link href={getDomainUrl()}>
									<div className={styles.detail.tag.container()}>
										<div className={styles.detail.tag()}>{project.domain}</div>
									</div>
								</Link>
							</>
						)}
					</div>
				</ContentsidebarSidebar>
			</Contentsidebar>
			<Theme.OuterContainer>
				<Share />
				{relatedBlock && <Related {...relatedBlock} />}
			</Theme.OuterContainer>
		</div>
	)
}
